import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Spinner, Input, Modal, ModalBody } from "reactstrap";
import api from "../api/api";
import MainLayout from "../components/layouts/MainLayout";
import "../assets/scss/PremieresPage.scss"; // Import styles

const PremieresPage = ({ type = "tv" }) => {
    const [premieres, setPremieres] = useState([]);
    const [filteredPremieres, setFilteredPremieres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [executedSearchTerm, setExecutedSearchTerm] = useState(""); // Actual search trigger
    
    // Filtros
    const [availablePlatforms, setAvailablePlatforms] = useState([]); // Array of objects { name, logo_path }
    const [selectedPlatform, setSelectedPlatform] = useState(null); // null = All, or platform Name
    const [availableGenres, setAvailableGenres] = useState([]); // Set of genres
    const [selectedGenre, setSelectedGenre] = useState(null); // null = All
    const [sortOrder, setSortOrder] = useState("desc");

    // Modal State
    const [selectedPremiere, setSelectedPremiere] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Notification State
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success', title: '' });

    const showNotification = (title, message, type = 'success') => {
        setNotification({ show: true, title, message, type });
        setTimeout(() => {
            setNotification(prev => ({ ...prev, show: false }));
        }, 3500);
    };

    // Reset state when type changes
    useEffect(() => {
        setPremieres([]);
        setFilteredPremieres([]);
        setLoading(true);
        setSearchTerm("");
        setSelectedPlatform(null);
    }, [type]);

    useEffect(() => {
        const fetchPremieres = async () => {
            setLoading(true);
            try {
                // Determine requests based on type
                const promises = [api.get("/platform-premieres", { params: { type } })];
                
                // Only fetch user series if we are looking at TV shows (to filter out followed ones)
                if (type === 'tv') {
                    promises.push(api.get("/series"));
                }

                const responses = await Promise.all(promises);
                const premieresResponse = responses[0];
                const userSeriesResponse = type === 'tv' ? responses[1] : null;

                if (premieresResponse.data && premieresResponse.data.operation === "SUCCESS") {
                    let data = premieresResponse.data.premieres || [];
                    
                    // Filter out followed series only if type is TV
                    if (type === 'tv' && userSeriesResponse && userSeriesResponse.data && userSeriesResponse.data.operation === "SUCCESS") {
                        const userSeriesNames = new Set(
                            (userSeriesResponse.data.series || []).map(s => s.nom ? s.nom.toLowerCase() : "")
                        );
                        
                        // Filter: Exclude if title is in userSeriesNames
                        data = data.filter(p => p && p.title && !userSeriesNames.has(p.title.toLowerCase())); // Defensive check
                    }

                    setPremieres(data);
                    
                    // Extraer plataformas únicas y géneros únicos
                    const platformsMap = new Map();
                    const genresSet = new Set();

                    data.forEach(item => {
                        if (!item) return; // Safety check

                        // Extract Platforms
                        if (item.platforms && Array.isArray(item.platforms)) {
                            item.platforms.forEach(p => {
                                if (p && p.name && !platformsMap.has(p.name)) {
                                    platformsMap.set(p.name, p.logo_path);
                                }
                            });
                        }
                        // Extract Genres
                        if (item.genres && Array.isArray(item.genres)) {
                            item.genres.forEach(g => genresSet.add(g));
                        }
                    });
                    
                    // Convert map to array for platforms
                    const platformsArray = Array.from(platformsMap, ([name, logo_path]) => ({ name, logo_path }));
                    platformsArray.sort((a, b) => a.name.localeCompare(b.name));
                    
                    // Convert Set to array for genres
                    const genresArray = Array.from(genresSet).sort();

                    setAvailablePlatforms(platformsArray);
                    setAvailableGenres(genresArray);
                    setFilteredPremieres(data);
                } else {
                     console.error("Error fetching premieres:", premieresResponse.data.message);
                }
            } catch (err) {
                console.error("Network error fetching premieres or user series:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPremieres();
    }, [type]); // Re-run when type changes

    // Unified Effect for Filtering and Searching
    // Unified Effect for Filtering and Searching
    useEffect(() => {
        const performSearch = async () => {
             if (!executedSearchTerm.trim()) {
                // Modo Normal (Local Filter)
                // If there's NO executed search term, we show default premieres (filtered by genre/platform locally)
                
                // NOTE: If user typed but didn't hit enter, 'executedSearchTerm' is empty.
                // We want to filter LOCAL premieres by 'executedSearchTerm' if strictly matching?
                // Actually, if 'executedSearchTerm' is empty, we just show all premieres.
                // BUT user might want to filter local list by typing + Enter.
                
                // Let's assume 'executedSearchTerm' drives everything now.
                
                let results = [...premieres];

                // 2. Filtro por Plataforma (Visual)
                if (selectedPlatform) {
                    results = results.filter(item => 
                        item.platforms && item.platforms.some(p => p.name === selectedPlatform)
                    );
                }

                // 3. Filtro por Género (Visual - Pills)
                if (selectedGenre) {
                    results = results.filter(item => 
                        item.genres && Array.isArray(item.genres) && item.genres.includes(selectedGenre)
                    );
                }

                // 4. Orden por Fecha
                results.sort((a, b) => {
                    const dateA = new Date(a.first_air_date || 0);
                    const dateB = new Date(b.first_air_date || 0);
                    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
                });

                setFilteredPremieres(results);

             } else {
                 // Modo Búsqueda (Global)
                 // Si hay texto confirmado, buscamos fuera.
                 // (Podríamos buscar dentro también, pero el usuario pidió "buscar en la BBDD de TMDB")
                 
                 setLoading(true);
                 try {
                    const response = await api.get('/tmdb/search', { 
                        params: { query: executedSearchTerm, type } 
                    });
                    
                    if (response.data.operation === "SUCCESS") {
                        let searchResults = response.data.results || [];

                        // Optional: Apply Genre filter to search results too?
                        if (selectedGenre) {
                             searchResults = searchResults.filter(item => 
                                item.genres && Array.isArray(item.genres) && item.genres.includes(selectedGenre)
                            );
                        }
                        
                        setFilteredPremieres(searchResults);
                    }
                } catch (error) {
                    console.error("Error searching remote TMDB:", error);
                    showNotification("Error", "No s'ha pogut realitzar la cerca global.", "error");
                } finally {
                    setLoading(false);
                }
             }
        };

        performSearch();

    }, [executedSearchTerm, selectedPlatform, selectedGenre, sortOrder, premieres, type]);

    const handlePlatformClick = (platformName) => {
        if (selectedPlatform === platformName) {
            setSelectedPlatform(null); // Toggle off
        } else {
            setSelectedPlatform(platformName);
        }
    };

    const handleCardClick = async (item) => {
        try {
          // Fetch detailed data with type
          const response = await api.get(`/tmdb/${item.id}`, { params: { type } });
          if (response.data.operation === "SUCCESS") {
            const detailedItem = { ...item, ...response.data.details };
            setSelectedPremiere(detailedItem);
            setModalOpen(true);
          } else {
            console.error("Error fetching detailed data:", response.data.message);
            // Fallback
            setSelectedPremiere(item);
            setModalOpen(true);
          }
        } catch (error) {
          console.error("Error calling /tmdb/:id endpoint:", error);
          // Fallback
          setSelectedPremiere(item);
          setModalOpen(true);
        }
    };

    const handleAddSeries = async () => {
        if (!selectedPremiere || type !== 'tv') return; // Only for TV currently

        try {
            const payload = {
                name: selectedPremiere.title,
                imatge: selectedPremiere.posterUrl, 
                definicio: '[HDTV 720p]',
                capitol: 1,
                temporada: 1,
                capitolsTemporada: selectedPremiere.number_of_episodes || 0
            };

            const response = await api.post('/series', payload);
            if (response.status === 201 || response.data.operation === 'SUCCESS') {
                showNotification('Èxit!', `Has començat a seguir "${selectedPremiere.title}"`, 'success');
                
                // Remove from local list
                setPremieres(prev => prev.filter(s => s.title !== selectedPremiere.title));
                
                setModalOpen(false); 
            }
        } catch (error) {
            console.error("Error adding series:", error);
            if (error.response && error.response.data && error.response.data.message) {
                 showNotification('Error', error.response.data.message, 'error');
            } else {
                 showNotification('Error', 'No s\'ha pogut afegir la sèrie.', 'error');
            }
        }
    };

    const handleAddMovie = async () => {
        if (!selectedPremiere || type !== 'movie') return;

        try {
            const payload = {
                nom: selectedPremiere.title,
                tmdb_id: selectedPremiere.id,
                image: selectedPremiere.posterUrl,
                backdrop: selectedPremiere.backdrop_path ? `https://image.tmdb.org/t/p/original${selectedPremiere.backdrop_path}` : "",
                year: selectedPremiere.first_air_date ? parseInt(selectedPremiere.first_air_date.split('-')[0]) : null,
                runtime: selectedPremiere.runtime,
                overview: selectedPremiere.overview,
                genero: selectedPremiere.genres && selectedPremiere.genres.length > 0 
                        ? (typeof selectedPremiere.genres[0] === 'string' ? selectedPremiere.genres[0] : selectedPremiere.genres[0].name) 
                        : "",
            };

            const response = await api.post('/movies', payload);
            if (response.status === 201 || response.data.operation === 'SUCCESS') {
                showNotification('Èxit!', `Has afegit "${selectedPremiere.title}" a la teua llista.`, 'success');
                setModalOpen(false);
                
                // Remove from local list
                setPremieres(prev => prev.filter(p => p.id !== selectedPremiere.id));
            }
        } catch (error) {
            console.error("Error adding movie:", error);
            if (error.response && error.response.data && error.response.data.message) {
                 showNotification('Error', error.response.data.message, 'error');
            } else {
                 showNotification('Error', 'No s\'ha pogut afegir la pel·lícula.', 'error');
            }
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };

    return (
        <MainLayout>
             {/* Custom Notification Toast */}
             <div className={`premium-toast ${notification.show ? 'show' : ''} ${notification.type}`}>
                <i className={`bi ${notification.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}></i>
                <div className="toast-content">
                    <strong>{notification.title}</strong>
                    <span>{notification.message}</span>
                </div>
            </div>

            <Container fluid>
                {/* Header Row */}
                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-center mb-0 gap-3 premieres-header-row">
                    <h4 className="card-title fw-bold text-uppercase font-size-18 mb-0 text-dark">
                        Novetats: {type === 'tv' ? 'Sèries' : 'Pel·lícules'}
                    </h4>
                    
                    <div className="premieres-filter-container">
                         {/* Search */}
                        <div className="premium-search-box">
                            <Input 
                                type="text" 
                                placeholder="Buscar títol (Prem Intro)..." 
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    if (e.target.value === "") setExecutedSearchTerm(""); // Reset on clear
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setExecutedSearchTerm(searchTerm);
                                    }
                                }}
                                className="form-control"
                            />
                            <i className="bi bi-search"></i>
                        </div>

                        {/* Date Order (Sort) Button */}
                        <button
                            className="btn-premium-sort"
                            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                            title={sortOrder === 'desc' ? "Orde: Més recents primer" : "Orde: Més antigues primer"}
                        >
                            <i className={`bi ${sortOrder === 'desc' ? 'bi-sort-numeric-down-alt' : 'bi-sort-numeric-up'}`}></i>
                            <span>{sortOrder === 'desc' ? 'Més recents' : 'Més antigues'}</span>
                        </button>
                    </div>
                </div>

                {/* Genre Filter Scroll (New - Dashboard Style) */}
                {availableGenres.length > 0 && (
                     <div className="d-flex justify-content-center mb-4 mt-4"> {/* Centered Container */}
                        <div 
                            className="d-flex align-items-center bg-white border px-3 gap-2"
                            style={{ 
                                height: '54px', 
                                borderRadius: '27px', 
                                maxWidth: '100%', 
                                minWidth: '0', 
                                width: 'auto', // Allow it to shrink/grow based on content up to max width
                                flexShrink: 1,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                            }}
                        >
                            {/* Icon */}
                            <div className="text-muted d-flex align-items-center pe-3 border-end flex-shrink-0">
                                <i className="bi bi-funnel-fill text-muted" style={{ fontSize: '1.1rem' }}></i>
                            </div>

                            {/* Scrollable Tags Area */}
                            <div 
                                className="d-flex align-items-center gap-2 py-2" 
                                style={{ 
                                    flex: 1,
                                    minWidth: 0,
                                    overflowX: 'auto', 
                                    whiteSpace: 'nowrap', 
                                    scrollbarWidth: 'none', 
                                    msOverflowStyle: 'none', 
                                    WebkitOverflowScrolling: 'touch',
                                    maskImage: 'linear-gradient(to right, black 90%, transparent 100%)', 
                                    WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)'
                                }}
                            >
                                <style>{`
                                    .platform-icons-grid { margin-top: 1rem !important; }
                                    .no-scrollbar::-webkit-scrollbar { display: none; }
                                `}</style>
                                
                                <button 
                                    onClick={() => setSelectedGenre(null)}
                                    className={`btn btn-sm d-flex align-items-center transition-all flex-shrink-0 ${
                                        selectedGenre === null
                                        ? 'bg-dark text-white border-dark' 
                                        : 'bg-light text-secondary border-0 hover-bg-gray'
                                    }`}
                                    style={{ 
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        borderRadius: '50px', 
                                        padding: '0.25rem 0.8rem',
                                    }}
                                >
                                    Tots
                                </button>

                                {availableGenres.map(genre => {
                                const isSelected = selectedGenre === genre;
                                return (
                                    <button
                                        key={genre}
                                        onClick={() => setSelectedGenre(isSelected ? null : genre)}
                                        className={`btn btn-sm d-flex align-items-center transition-all flex-shrink-0 ${
                                            isSelected 
                                                ? 'bg-dark text-white border-dark' 
                                                : 'bg-light text-secondary border-0 hover-bg-gray'
                                        }`}
                                        style={{ 
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            borderRadius: '50px', 
                                            padding: '0.25rem 0.8rem',
                                        }}
                                    >
                                        {genre}
                                    </button>
                                );
                            })}
                            </div>
                        </div>
                    </div>
                )}

                {/* VISUAL PLATFORM FILTER GRID */}
                {availablePlatforms.length > 0 && (
                    <div className="platform-icons-grid">
                        <button 
                            className={`platform-icon-btn ${selectedPlatform === null ? 'active' : ''}`}
                            onClick={() => setSelectedPlatform(null)}
                            title="Totes les plataformes"
                            style={{ width: 'auto', padding: '0 1rem', fontSize: '0.9rem', fontWeight: 'bold' }}
                        >
                            TOTES
                        </button>
                        {availablePlatforms.map((p) => (
                            <button
                                key={p.name}
                                className={`platform-icon-btn ${selectedPlatform === p.name ? 'active' : ''}`}
                                onClick={() => handlePlatformClick(p.name)}
                                title={p.name}
                            >
                                {p.logo_path ? (
                                    <img 
                                        src={`https://image.tmdb.org/t/p/original${p.logo_path}`} 
                                        alt={p.name || 'Platform'} 
                                        onError={(e) => {e.target.style.display='none';}}
                                    />
                                ) : (
                                    <span style={{fontSize:'0.7rem', textAlign:'center'}}>{(p.name || '').substring(0,3)}</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    </div>
                ) : (
                    <Row>
                        {filteredPremieres.length > 0 ? (
                            filteredPremieres.map((item, index) => {
                                if (!item) return null; // Safety check
                                const primaryPlatform = item.platforms && item.platforms.length > 0 ? item.platforms[0] : null;

                                return (
                                    <Col key={index} xl={2} lg={3} md={4} sm={6} xs={12} className="mb-4">
                                        <Card 
                                            className="h-100 shadow-sm border-0 serie-card overflow-hidden" 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleCardClick(item)}
                                        >
                                            <div className="position-relative">
                                                 {/* Poster Image */}
                                                <div style={{ aspectRatio: '2/3', overflow: 'hidden' }}>
                                                    <img 
                                                        src={item.posterUrl || "https://via.placeholder.com/300x450?text=No+Image"} 
                                                        alt={item.title} 
                                                        className="w-100 h-100"
                                                        style={{ objectFit: 'cover' }}
                                                    />
                                                </div>

                                                {/* Platform Badge Overlay */}
                                                {primaryPlatform && (
                                                    <div className="position-absolute top-0 end-0 m-2">
                                                        {primaryPlatform.logo_path ? (
                                                            <img 
                                                                src={`https://image.tmdb.org/t/p/w45${primaryPlatform.logo_path}`} 
                                                                alt={primaryPlatform.name} 
                                                                className="rounded-circle shadow-sm bg-white p-1"
                                                                style={{ width: '30px', height: '30px' }}
                                                                title={primaryPlatform.name}
                                                            />
                                                        ) : (
                                                            <Badge color="primary">{primaryPlatform.name}</Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })
                        ) : (
                            <Col className="text-center py-5">
                                <div className="text-muted">
                                    <i className="bi bi-film display-4 mb-3 d-block"></i>
                                    <h5>No s'han trobat novetats.</h5>
                                    {searchTerm && <p>Prova amb una altra cerca.</p>}
                                </div>
                            </Col>
                        )}
                    </Row>
                )}
            </Container>

            {/* Detail Modal */}
            <Modal 
                isOpen={modalOpen} 
                toggle={toggleModal} 
                centered 
                size="lg" 
                className="premium-serie-modal"
                contentClassName="border-0"
            >
                <ModalBody className="p-0">
                    {selectedPremiere && (
                        <div 
                            className="modal-backdrop-hero"
                            style={{ 
                                backgroundImage: selectedPremiere.backdrop_path 
                                    ? `url(https://image.tmdb.org/t/p/w1280${selectedPremiere.backdrop_path})` 
                                    : 'none'
                            }}
                        >
                            <button className="modal-close-btn" onClick={toggleModal}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                             <Row className="align-items-center">
                                <Col md={4} className="mb-4 mb-md-0 d-flex justify-content-center justify-content-md-start">
                                    <div className="poster-container">
                                        <img 
                                            src={selectedPremiere.posterUrl || "https://via.placeholder.com/300x450?text=No+Image"} 
                                            alt={selectedPremiere.title} 
                                        />
                                    </div>
                                </Col>
                                <Col md={8}>
                                    <div className="content-section text-shadow">
                                        <h2 className="serie-title-large mb-2">{selectedPremiere.title}</h2>
                                        {selectedPremiere.original_name && selectedPremiere.original_name !== selectedPremiere.title && (
                                            <h5 className="text-white-50 mb-3 fst-italic">{selectedPremiere.original_name}</h5>
                                        )}
                                        
                                        <div className="meta-row">
                                            {selectedPremiere.vote_average > 0 && (
                                                <span className="meta-tag rating-tag">
                                                    <i className="bi bi-star-fill"></i> {selectedPremiere.vote_average.toFixed(1)}
                                                </span>
                                            )}

                                            <span className="meta-tag">
                                                <i className="bi bi-calendar-event me-2"></i>
                                                {selectedPremiere.first_air_date ? selectedPremiere.first_air_date.split('-')[0] : 'N/A'}
                                            </span>
                                            
                                            {/* TV Specific Metadata */}
                                            {type === 'tv' && selectedPremiere.number_of_seasons && (
                                                <span className="meta-tag bg-white text-dark border">
                                                    {selectedPremiere.number_of_seasons} Temporades
                                                </span>
                                            )}
                                            {type === 'tv' && selectedPremiere.number_of_episodes && (
                                                <span className="meta-tag bg-white text-dark border">
                                                    {selectedPremiere.number_of_episodes} Caps.
                                                </span>
                                            )}

                                            {/* Movie Specific Metadata */}
                                            {type === 'movie' && selectedPremiere.runtime > 0 && (
                                                <span className="meta-tag bg-white text-dark border">
                                                    {Math.floor(selectedPremiere.runtime / 60)}h {selectedPremiere.runtime % 60}min
                                                </span>
                                            )}

                                            {selectedPremiere.status && (
                                                <span className={`meta-tag status-tag ${selectedPremiere.status === 'Ended' || selectedPremiere.status === 'Released' ? 'status-ended' : 'status-returning'}`}>
                                                    {selectedPremiere.status === 'Ended' ? 'Finalitzada' : selectedPremiere.status === 'Released' ? 'Estrenada' : 'En Emisió'}
                                                </span>
                                            )}
                                        </div>

                                        {selectedPremiere.genres && (
                                            <div className="mb-3">
                                                {selectedPremiere.genres.map(g => (
                                                    <span key={g.id} className="meta-tag genre-tag me-2 mb-2 d-inline-block">
                                                        {g.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <span className="section-label text-white-50">Sinopsi</span>
                                        <p className="synopsis-text text-white">
                                            {selectedPremiere.overview || "No hi ha descripció disponible."}
                                        </p>

                                        {selectedPremiere.platforms && selectedPremiere.platforms.length > 0 && (
                                            <>
                                                <span className="section-label text-white-50">Disponible en</span>
                                                <div className="platform-pill-container mb-4">
                                                    {selectedPremiere.platforms.map((p, idx) => (
                                                        <div key={idx} className="platform-pill">
                                                            {p.logo_path ? (
                                                                <img 
                                                                    src={`https://image.tmdb.org/t/p/w45${p.logo_path}`} 
                                                                    alt={p.name} 
                                                                    width="32" 
                                                                    height="32"
                                                                />
                                                            ) : (
                                                                <span className="badge bg-secondary me-2 rounded-circle p-2">{(p.name || "U").substring(0,1)}</span>
                                                            )}
                                                            <span>{p.name || "Unknown"}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {/* ACTION BUTTONS (Always Visible) */}
                                        <div className="d-flex mt-3">
                                            {/* Add Button - ONLY FOR TV */}
                                            {type === 'tv' && (
                                                <button 
                                                    className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-bold shadow-sm"
                                                    onClick={() => handleAddSeries()}
                                                    title="Seguir esta sèrie"
                                                    style={{ fontSize: '1rem' }}
                                                >
                                                    <i className="bi bi-plus-lg"></i>
                                                    <span>Seguir</span>
                                                </button>
                                            )}

                                            {/* Add Button - ONLY FOR MOVIES */}
                                            {type === 'movie' && (
                                                <button 
                                                    className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-bold shadow-sm"
                                                    onClick={() => handleAddMovie()}
                                                    title="M'interessa"
                                                    style={{ fontSize: '1rem' }}
                                                >
                                                    <i className="bi bi-bookmark-plus"></i>
                                                    <span>M'interessa</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </MainLayout>
    );
};

export default PremieresPage;
